// LeaveRecord (Session ASZ, D-V1): the client half. A minimal record of an engineer's holiday, sick
// or other leave, office/admin only. Mirrors server/src/modules/leave/leave.ts.
//
// No allowance tracking, no approval workflow, no accrual. Its purpose is so the standby rota page
// and dispatch (Scheduling, Call-outs) can WARN when the office puts someone on call or sends them to
// a job during their own recorded leave -- never blocks either.
import { apiRequest } from "./api";

export const LEAVE_TYPES = ["holiday", "sick", "other"] as const;
export type LeaveType = (typeof LEAVE_TYPES)[number];

export const LEAVE_TYPE_LABEL: Record<LeaveType, string> = {
  holiday: "Holiday",
  sick: "Sick leave",
  other: "Other",
};

export interface LeaveRecord {
  id: string;
  engineerId: string;
  engineerName: string;
  startDate: string;
  endDate: string;
  type: LeaveType;
  notes: string | null;
  recordedByName: string;
  archived: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface LeaveRecordInput {
  engineerId: string;
  startDate: string;
  endDate: string;
  type: LeaveType;
  notes?: string | null;
}

export const leaveApi = {
  // Always read fresh (noCache): the list feeds the page the office edits.
  list: async (query: { engineerId?: string } = {}, includeArchived = false): Promise<LeaveRecord[]> => {
    const qs = new URLSearchParams();
    if (query.engineerId) qs.set("engineerId", query.engineerId);
    if (includeArchived) qs.set("includeArchived", "true");
    const suffix = qs.toString();
    const res = await apiRequest<{ leaveRecords: LeaveRecord[] }>(`/leave-records${suffix ? `?${suffix}` : ""}`, { noCache: true });
    return res.leaveRecords;
  },
  create: (body: LeaveRecordInput) => apiRequest<LeaveRecord>("/leave-records", { method: "POST", body: JSON.stringify(body) }),
  update: (id: string, body: Partial<Omit<LeaveRecordInput, "engineerId">>) =>
    apiRequest<LeaveRecord>(`/leave-records/${encodeURIComponent(id)}`, { method: "PATCH", body: JSON.stringify(body) }),
  archive: (id: string) => apiRequest<{ ok: true; archived: true }>(`/leave-records/${encodeURIComponent(id)}`, { method: "DELETE" }),
  restore: (id: string) => apiRequest<LeaveRecord>(`/leave-records/${encodeURIComponent(id)}/restore`, { method: "POST" }),
  deletePermanently: (id: string) => apiRequest<{ ok: true; deleted: true }>(`/leave-records/${encodeURIComponent(id)}?permanent=true`, { method: "DELETE" }),
};
